import { useState } from "react";
import { Button, ButtonProps } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FileText, AlertCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SupplementWorkspaceButtonProps {
  jobId: string;
  jobType: 'insurance' | 'retail' | 'commercial' | string;
  hasEstimate: boolean;
  hasPhotos: boolean;
  isProcessing: boolean;
  photos?: Array<{ id: string; url: string; ai_analyzed: boolean; ai_damage_detected: boolean }>;
  onAnalysisComplete?: () => void;
  onNavigateToSupplements?: () => void;
  
  // Optional styling props
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  className?: string;
}

export function SupplementWorkspaceButton({
  jobId,
  jobType,
  hasEstimate,
  hasPhotos,
  isProcessing,
  photos = [],
  onAnalysisComplete,
  onNavigateToSupplements,
  variant = "outline",
  size = "sm",
  className,
}: SupplementWorkspaceButtonProps) {
  const [showMissingDataDialog, setShowMissingDataDialog] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [totalPhotos, setTotalPhotos] = useState(0);

  // Don't show button for non-insurance jobs
  if (jobType.toLowerCase() !== 'insurance') {
    return null;
  }

  const isReady = hasEstimate && hasPhotos && !isProcessing;
  const isDisabled = !isReady || analyzing;

  const handleAnalyzePhotos = async () => {
    try {
      setAnalyzing(true);
      
      // Get photos that haven't been analyzed yet
      const photosToAnalyze = photos.filter(p => !p.ai_analyzed);
      
      if (photosToAnalyze.length === 0) {
        toast.success("All photos analyzed", {
          description: "Navigating to workspace...",
        });
        if (onNavigateToSupplements) {
          onNavigateToSupplements();
        }
        return;
      }

      setTotalPhotos(photosToAnalyze.length);
      toast.info(`Analyzing ${photosToAnalyze.length} photo(s)...`);

      // Analyze photos one by one
      for (let i = 0; i < photosToAnalyze.length; i++) {
        const photo = photosToAnalyze[i];
        setCurrentPhotoIndex(i + 1);

        try {
          const { error: analyzeError } = await supabase.functions.invoke('analyze-damage', {
            body: { 
              photoId: photo.id,
              photoUrl: photo.url
            }
          });

          if (analyzeError) {
            console.error(`Analysis failed for photo ${photo.id}:`, analyzeError);
          }
        } catch (error) {
          console.error(`Analysis error for photo ${photo.id}:`, error);
        }
      }

      toast.success(`Successfully analyzed ${photosToAnalyze.length} photo(s)`);

      if (onAnalysisComplete) {
        onAnalysisComplete();
      }

      // Navigate to workspace after analysis
      setTimeout(() => {
        if (onNavigateToSupplements) {
          onNavigateToSupplements();
        }
      }, 1000);

    } catch (error: any) {
      console.error('Error analyzing photos:', error);
      toast.error(error.message || "Failed to analyze photos");
    } finally {
      setAnalyzing(false);
      setCurrentPhotoIndex(0);
      setTotalPhotos(0);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!hasEstimate || !hasPhotos) {
      setShowMissingDataDialog(true);
      return;
    }

    if (isReady) {
      handleAnalyzePhotos();
    }
  };

  const getButtonClasses = () => {
    if (!hasEstimate || !hasPhotos) {
      return "bg-muted text-muted-foreground cursor-not-allowed opacity-60 hover:bg-muted hover:scale-100";
    }
    if (analyzing) {
      return "bg-blue-500 text-white animate-pulse shadow-[0_0_20px_rgba(59,130,246,0.5)] hover:bg-blue-500";
    }
    if (isProcessing) {
      return "bg-blue-500 text-white animate-pulse shadow-[0_0_20px_rgba(59,130,246,0.5)] hover:bg-blue-500";
    }
    return "bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-[0_0_30px_rgba(147,51,234,0.4)] hover:shadow-[0_0_40px_rgba(147,51,234,0.6)] hover:scale-105 transition-all border-0";
  };

  const getButtonLabel = () => {
    if (analyzing) {
      return `Analyzing ${currentPhotoIndex}/${totalPhotos}...`;
    }
    if (isProcessing) {
      return "Processing...";
    }
    return "Analyze for Supplements";
  };

  const getButtonIcon = () => {
    if (analyzing || isProcessing) {
      return <Loader2 className="h-4 w-4 mr-1 animate-spin" />;
    }
    return <FileText className="h-4 w-4 mr-1" />;
  };

  const getMissingItems = () => {
    const missing: string[] = [];
    if (!hasEstimate) missing.push("Xactimate Estimate");
    if (!hasPhotos) missing.push("Property Photos");
    return missing;
  };

  const button = (
    <Button
      size={size}
      variant={variant}
      onClick={handleClick}
      disabled={isDisabled}
      className={cn(getButtonClasses(), className)}
    >
      {getButtonIcon()}
      {getButtonLabel()}
    </Button>
  );

  // Show tooltip for disabled state
  if (!hasEstimate || !hasPhotos) {
    return (
      <>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {button}
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">Missing: {getMissingItems().join(", ")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <AlertDialog open={showMissingDataDialog} onOpenChange={setShowMissingDataDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-warning" />
                Upload Required Files
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-4">
                <p>
                  Please upload your estimate and photos to use the AI Supplement Workspace.
                </p>
                
                <div className="bg-muted p-3 rounded-lg space-y-2">
                  <p className="font-semibold text-foreground text-sm">Missing items:</p>
                  <ul className="space-y-1">
                    {getMissingItems().map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span className="text-destructive">❌</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => setShowMissingDataDialog(false)}>
                Got it
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  return button;
}
